import { logError, logWarn } from '@ptek/client-logger';
import { SessionDocument } from '../api-clients/sessions';
import { GeneratedDocument } from '../types/document';
import { TranscriptLine } from '../types/recorder';
import {
  getGeneratedDocuments,
  getTranscriptDocument,
  ParsedTranscriptSegment,
  TranscriptProcessor,
} from './transcriptUtils';

export interface CachedDocument {
  id: string;
  templateId: string;
  templateName: string;
  content: string;
  status: string;
  createdAt: string;
  sessionId: string;
}

export interface ProcessedSessionData {
  success: boolean;
  transcriptLines: TranscriptLine[];
  documents: GeneratedDocument[];
  sessionId: string;
  error?: string;
  transcriptDocumentId?: string;
  transcriptIsEdited?: boolean;
  transcriptUpdatedAt?: string;
  transcriptUpdatedBy?: string;
  editOverrides?: Record<string, { text: string; editedAt: string; editedBy: string }>;
}

export interface PopulationResult {
  success: boolean;
  sessionId?: string;
  error?: string;
}
export function processTranscriptData(documents: SessionDocument[]): {
  success: boolean;
  transcriptLines: TranscriptLine[];
  error?: string;
} {
  try {
    const transcriptDoc = getTranscriptDocument(documents);
    if (!transcriptDoc) {
      logWarn('sessionDataPopulator', 'No transcript document found');
      return {
        success: true, // Not an error, just no transcript
        transcriptLines: [],
      };
    }

    const parsedData = TranscriptProcessor.parseTranscriptContent(
      transcriptDoc.content
    );
    if (!parsedData) {
      // Segments missing — try to recover transcript lines from editOverrides
      try {
        const raw = JSON.parse(transcriptDoc.content);
        if (raw?.editOverrides && typeof raw.editOverrides === 'object') {
          const overrideEntries: Array<[string, { text?: string }]> =
            Object.entries(raw.editOverrides);
          if (overrideEntries.length > 0) {
            const recovered: TranscriptLine[] = overrideEntries
              .sort(([a], [b]) => a.localeCompare(b))
              .filter(([, v]) => typeof v?.text === 'string')
              .map(([segId, v]) => ({
                timestamp: 0,
                text: v.text as string,
                confidence: 1,
                segmentId: segId,
              }));
            if (recovered.length > 0) {
              logWarn(
                'sessionDataPopulator',
                'Recovered transcript lines from editOverrides (segments missing)',
                { recoveredCount: recovered.length }
              );
              return { success: true, transcriptLines: recovered };
            }
          }
        }
      } catch {
        // Not valid JSON — fall through
      }

      logError(
        'sessionDataPopulator',
        'Failed to parse transcript content',
        {
          contentLength: transcriptDoc.content?.length,
        }
      );
      return {
        success: false,
        transcriptLines: [],
        error: 'Failed to parse transcript content',
      };
    }

    const transcriptLines: TranscriptLine[] = parsedData.segments.map(
      (segment: ParsedTranscriptSegment) => ({
        timestamp: segment.timestamp,
        text: segment.text,
        confidence: segment.confidence,
        segmentId: segment.id,
      })
    );

    return { success: true, transcriptLines };
  } catch (error) {
    logError('sessionDataPopulator', 'Error processing transcript:', error);
    return {
      success: false,
      transcriptLines: [],
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export function processDocumentData(rawDocuments: SessionDocument[]): {
  success: boolean;
  documents: GeneratedDocument[];
  firstDocumentTemplateId?: string;
  error?: string;
} {
  try {
    const generatedDocs = getGeneratedDocuments(rawDocuments);

    if (generatedDocs.length === 0) {
      return { success: true, documents: [] };
    }

    const formattedDocs = Object.values(
      generatedDocs.reduce(
        (acc, doc) => {
          const formattedDoc: GeneratedDocument = {
            id: doc.id,
            templateId: doc.templateId || doc.type,
            templateName: doc.title || mapDocumentTypeToName(doc.type),
            content: doc.content,
            status: 'completed',
            createdAt: doc.generatedAt,
          };

          const templateId = formattedDoc.templateId || 'unknown';
          const existing = acc[templateId];

          if (
            !existing ||
            new Date(formattedDoc.createdAt) > new Date(existing.createdAt)
          ) {
            acc[templateId] = formattedDoc;
          }

          return acc;
        },
        {} as Record<string, GeneratedDocument>
      )
    );

    const orderedDocs = formattedDocs.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return {
      success: true,
      documents: orderedDocs,
    };
  } catch (error) {
    logError('sessionDataPopulator', 'Error processing document data:', error);
    return {
      success: false,
      documents: [],
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export function mapDocumentTypeToName(type: string): string {
  switch (type) {
    case 'chart_note':
      return 'Chart Note';
    case 'exercise_plan':
      return 'Exercise Plan';
    case 'referral_letter':
      return 'Referral Letter';
    case 'transcript':
      return 'Transcript';
    case 'SOAP_NOTE':
      return 'SOAP Note';
    default:
      return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }
}

export function processSessionData(
  sessionId: string,
  documents: SessionDocument[]
): ProcessedSessionData {
  try {
    const transcriptResult = processTranscriptData(documents);
    const documentResult = processDocumentData(documents);
    const transcriptDoc = getTranscriptDocument(documents);

    const success = transcriptResult.success && documentResult.success;
    const error = transcriptResult.error || documentResult.error;

    let editOverrides: Record<string, { text: string; editedAt: string; editedBy: string }> | undefined;
    if (transcriptDoc?.content) {
      try {
        const parsed = JSON.parse(transcriptDoc.content);
        if (parsed?.editOverrides && Object.keys(parsed.editOverrides).length > 0) {
          editOverrides = parsed.editOverrides;
        }
      } catch {
        // Content is not valid JSON — ignore
      }
    }

    return {
      success,
      transcriptLines: transcriptResult.transcriptLines,
      documents: documentResult.documents,
      sessionId,
      error,
      transcriptDocumentId: transcriptDoc?.id,
      transcriptIsEdited: transcriptDoc?.isEdited || !!editOverrides,
      transcriptUpdatedAt: transcriptDoc?.updatedAt,
      transcriptUpdatedBy: transcriptDoc?.updatedBy,
      editOverrides,
    };
  } catch (error) {
    logError(
      'sessionDataPopulator',
      'Error in complete session data processing:',
      error
    );
    return {
      success: false,
      transcriptLines: [],
      documents: [],
      sessionId,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
